import React, { useState, useReducer } from 'react';
import styles from './reportMedicationSafetyError.module.scss';
import DropDown from '../../components/Dropdown';
import ReactTooltip from 'react-tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQuestionCircle } from '@fortawesome/free-solid-svg-icons/faQuestionCircle';
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons/faCircleNotch';
import ThankYouMessage from '../../components/ThankYouMessage';
import {
  checkMimeType,
  maxSelectFile,
  checkFileSize,
} from '../../util/fileUploadHelper';
import { withPrefix } from 'gatsby';
import Layout from '../../layout';
import { usageLogIds } from '../../util/config';
import { Helmet } from 'react-helmet';
import Banner from '@ecri/react-components/MemberPageBanner';

const ReportMedicationVaccineErrorForm = () => {
  const MB = 1000000;
  const sizePerFile = 12 * MB;
  const maxAllowedFiles = 3;
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState(new Map());
  const [validationMessage, setValidationMessage] = useState('');
  const [removeFileIds, setRemoveFileIds] = useState<string[]>([]);
  const [emailValidationMessage, setEmailValidationMessage] = useState('');
  const [firstNameValidationMessage, setFirstNameValidationMessage] = useState(
    ''
  );
  const [lastNameValidationMessage, setLastNameValidationMessage] = useState(
    ''
  );
  const [formValidationMessage, setFormValidationMessage] = useState('');
  const nameRegexValidator = /^[a-zA-z][^0-9!@#$%^&*()\[\]{}:;<>?\/~`_+=\\|]*$/;
  const [userInput, setUserInput] = useReducer(
    (state: any, newState: any) => ({ ...state, ...newState }),
    {
      firstName: '',
      lastName: '',
      email: '',
      confirmEmail: '',
      telephoneContact: '',
      eventTypeId: 0,
      eventHazardousCondition: '',
      causesContributingFactors: '',
      riskReductionStrategies: '',
      documents: [],
    }
  );
  const API_URL = `${process.env.GATSBY_API_URL ?? ''}${withPrefix('/api')}`;

  const normalizePhoneInput = (
    value: string,
    previousValue: string | any[]
  ) => {
    if (!value) return value;
    const currentValue = value.replace(/[^\d]/g, '');
    const currentLength = currentValue.length;

    if (!previousValue || value.length > previousValue.length) {
      if (currentLength < 4) return currentValue;
      if (currentLength < 7)
        return `${currentValue.slice(0, 3)}-${currentValue.slice(3)}`;
      return `${currentValue.slice(0, 3)}-${currentValue.slice(
        3,
        6
      )}-${currentValue.slice(6, 10)}`;
    }
  };

  const handleFormValueChange = (event: {
    target: { value: any; name?: any };
  }) => {
    const { name, value } = event.target;
    if (name === 'telephoneContact') {
      let phoneNumber = normalizePhoneInput(
        event.target.value,
        userInput.telephoneContact
      );
      setUserInput({ telephoneContact: phoneNumber });
    }
    if (name === 'email') {
      event.target.value !== userInput.confirmEmail
        ? setEmailValidationMessage(
            'The specified email addresses do not match.'
          )
        : setEmailValidationMessage('');
    }
    if (name === 'confirmEmail') {
      userInput.email !== event.target.value
        ? setEmailValidationMessage(
            'The specified email addresses do not match.'
          )
        : setEmailValidationMessage('');
    }
    if (name === 'firstName') {
      !nameRegexValidator.test(event.target.value)
        ? setFirstNameValidationMessage(
            'First Name should start with a letter and only contain letters, accented characters, special characters (such as comma, hyphen, apostrophe).'
          )
        : setFirstNameValidationMessage('');
    }
    if (name === 'lastName') {
      !nameRegexValidator.test(event.target.value)
        ? setLastNameValidationMessage(
            'Last Name should start with a letter and only contain letters, accented characters, special characters (such as comma, hyphen, apostrophe).'
          )
        : setLastNameValidationMessage('');
    }
    if (name !== 'telephoneContact') {
      setUserInput({ [name]: value });
    }
  };

  const handleEventTypeValueChange = (event: { value: any }) => {
    setUserInput({ eventTypeId: event.value });
  };

  const handleFileUploadChange = (event: any) => {
    const fileTypes = [
      'image/gif',
      'image/jpeg',
      'image/png',
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    ];
    setValidationMessage('');
    let files = event.target.files;
    const mimeType = checkMimeType(files, fileTypes);
    const fileSize = checkFileSize(files, sizePerFile);
    if (mimeType.isValid && fileSize.isValid) {
      let filesAll = [...files];

      filesAll.reduce((map, obj) => {
        setSelectedFiles(selectedFiles.set(obj.name, obj));
      }, {});

      const maxFiles = maxSelectFile(
        [...selectedFiles.values()],
        maxAllowedFiles
      );
      if (!maxFiles.isValid) {
        maxFiles.removedFiles.map((i: any) => {
          selectedFiles.delete(i);
          setSelectedFiles(selectedFiles);
        });
        event.target.value = null;
        setValidationMessage(
          'Maximum 3 Images/Files are allowed but ' +
            maxFiles.filesLength +
            ' were uploaded. The following files have been omitted as a result: ' +
            maxFiles.removedFiles.join(', ')
        );
      }
      setUserInput({ documents: [...selectedFiles.values()] });
    } else {
      if (!mimeType.isValid) {
        event.target.value = null;
        setValidationMessage(
          'The selected file ' +
            mimeType.fileName +
            ' cannot be uploaded. Files with the following extensions are allowed: gif, jpg, jpeg, png, pdf, doc, docx, xls, xlsx, ppt, pptx.'
        );
      }
      if (!fileSize.isValid) {
        event.target.value = null;
        setValidationMessage(
          fileSize.fileName +
            ' is too large, please pick a file smaller than ' +
            fileSize.Size / MB +
            'MB.'
        );
      }
      setUserInput({ documents: [...selectedFiles.values()] });
    }
  };

  const onSelectedFilesCheck = (id: any, event: any) => {
    if (event.target.checked) {
      const temp: any[] = [...removeFileIds];
      temp.push(id);
      setRemoveFileIds([...temp]);
    } else {
      const temp = removeFileIds.filter((x) => x !== id);
      setRemoveFileIds([...temp]);
    }
  };

  const handleRemoveSelectedFiles = () => {
    setValidationMessage('');
    removeFileIds.map((id) => {
      selectedFiles.delete(id);
    });
    setRemoveFileIds([]);
    setUserInput({ documents: [...selectedFiles.values()] });
  };

  const clearForm = () => {
    selectedFiles.clear();
    setUserInput({
      firstName: '',
      lastName: '',
      email: '',
      confirmEmail: '',
      telephoneContact: '',
      eventTypeId: 0,
      eventHazardousCondition: '',
      causesContributingFactors: '',
      riskReductionStrategies: '',
      documents: [],
    });
  };

  const handleChildClick = (
    newVal: boolean | ((prevState: boolean) => boolean)
  ) => {
    setIsSubmitted(newVal);
    setIsButtonDisabled(false);
    clearForm();
  };

  const handleSubmit = (event: { preventDefault: () => void }) => {
    event.preventDefault();
    const formValid =
      userInput.email === userInput.confirmEmail &&
      nameRegexValidator.test(userInput.firstName) &&
      nameRegexValidator.test(userInput.lastName);

    if (formValid) {
      setIsButtonDisabled(true);
      setValidationMessage('');
      setEmailValidationMessage('');
      setFirstNameValidationMessage('');
      setLastNameValidationMessage('');
      setFormValidationMessage('');

      const formData = new FormData();
      formData.append('FirstName', userInput.firstName);
      formData.append('LastName', userInput.lastName);
      formData.append('Email', userInput.email);
      formData.append('ConfirmEmail', userInput.confirmEmail);
      formData.append('TelephoneContact', userInput.telephoneContact);
      formData.append('EventTypeId', userInput.eventTypeId);
      formData.append(
        'EventHazardousCondition',
        userInput.eventHazardousCondition
      );
      formData.append(
        'CausesContributingFactors',
        userInput.causesContributingFactors
      );
      formData.append(
        'RiskReductionStrategies',
        userInput.riskReductionStrategies
      );

      userInput.documents.map((document: string | Blob, _index: any) => {
        formData.append('Files', document);
      });

      fetch(`${API_URL}/reporterror`, {
        method: 'POST',
        body: formData,
      })
        .then((resp) => resp.json())
        .then((data) => {
          if (data.success) {
            setIsSubmitted(true);
          } else {
            setIsButtonDisabled(false);
            setFormValidationMessage(
              'An error occurred while attempting to submit your report. The problem has been logged and will be investigated. For additional assistance, please contact ECRI and the ISMP PSO at psohelpdesk@ecri.org or (866)247-3004. Thank you for your patience.'
            );
          }
        })
        .catch((err) => {
          setIsButtonDisabled(false);
          setFormValidationMessage(
            'An error occurred while attempting to submit your report. The problem has been logged and will be investigated. For additional assistance, please contact ECRI and the ISMP PSO at psohelpdesk@ecri.org or (866)247-3004. Thank you for your patience.'
          );
        });
    } else if (userInput.email !== userInput.confirmEmail) {
      setEmailValidationMessage('The specified email addresses do not match.');
    } else if (!nameRegexValidator.test(userInput.firstName)) {
      setFirstNameValidationMessage(
        'First Name should start with a letter and only contain letters, accented characters, special characters (such as comma, hyphen, apostrophe).'
      );
    } else if (!nameRegexValidator.test(userInput.lastName)) {
      setLastNameValidationMessage(
        'Last Name should start with a letter and only contain letters, accented characters, special characters (such as comma, hyphen, apostrophe).'
      );
    }
  };

  if (isSubmitted) {
    return (
      <div>
        <Layout
          title="PSO Healthcare Practitioner Medication and Vaccine Event Report"
          analyticsSiteId={usageLogIds.memberSite}
        >
          <Helmet>
            <title>
              PSO Healthcare Practitioner Medication and Vaccine Event Report
            </title>
          </Helmet>
          <Banner title="PSO Healthcare Practitioner Medication and Vaccine Event Report"></Banner>
          <ThankYouMessage onClick={handleChildClick} data={userInput} />
        </Layout>
      </div>
    );
  }
  return (
    <div>
      <Layout
        title="PSO Healthcare Practitioner Medication and Vaccine Event Report"
        analyticsSiteId={usageLogIds.memberSite}
      >
        <Helmet>
          <title>
            PSO Healthcare Practitioner Medication and Vaccine Event Report
          </title>
        </Helmet>
        <Banner title="PSO Healthcare Practitioner Medication and Vaccine Event Report">
          <div className={styles.subText}>
            Use the form below to report a medication or vaccine related event
            to ECRI and the ISMP PSO
          </div>
        </Banner>

        <div className="page-content">
          <div className="container">
            <div className="col-xs-12 col-md-9 col-lg-9">
              <div className={`page-section ${styles.pageSection}`}>
                <p>
                  Please answer the questions as completely and accurately as
                  possible. Your answers will help us to better understand the
                  type of events that are happening, where and why they are
                  happening, and how to help those people being affected. We
                  will contact you if we have any questions after reviewing the report.
                </p>
              </div>
              <form
                onSubmit={handleSubmit}
                className={styles.reportMedicationErrorForm}
              >
                <div className="row">
                  <div className="col-xs-12 col-sm-6 col-lg-6">
                    <div className="formGroup">
                      <label className={styles.formLabel} htmlFor="firstName">
                        First Name{' '}
                        <span className={styles.formRequired}>*</span>
                      </label>
                      <input
                        className="formInput"
                        type="text"
                        name="firstName"
                        id="firstName"
                        value={userInput.firstName}
                        onChange={handleFormValueChange}
                        aria-label="First Name"
                        autoComplete="off"
                        required
                        maxLength={30}
                      />
                      {firstNameValidationMessage != '' && (
                        <div className={styles.alert}>
                          {firstNameValidationMessage}
                        </div>
                      )}
                    </div>
                    <div className="formGroup">
                      <label className={styles.formLabel} htmlFor="lastName">
                        Last Name <span className={styles.formRequired}>*</span>
                      </label>
                      <input
                        className="formInput"
                        type="text"
                        name="lastName"
                        id="lastName"
                        value={userInput.lastName}
                        onChange={handleFormValueChange}
                        aria-label="Last Name"
                        autoComplete="off"
                        required
                        maxLength={30}
                      />
                      {lastNameValidationMessage != '' && (
                        <div className={styles.alert}>
                          {lastNameValidationMessage}
                        </div>
                      )}
                    </div>
                    <div className="formGroup">
                      <label className={styles.formLabel} htmlFor="email">
                        Email <span className={styles.formRequired}>*</span>
                      </label>
                      <input
                        className="formInput"
                        name="email"
                        id="email"
                        type="email"
                        value={userInput.email}
                        onChange={handleFormValueChange}
                        aria-label="Email"
                        autoComplete="off"
                        required
                      />
                    </div>
                    <div className="formGroup">
                      <label
                        className={styles.formLabel}
                        htmlFor="confirmEmail"
                      >
                        Confirm Email{' '}
                        <span className={styles.formRequired}>*</span>
                      </label>
                      <input
                        className="formInput"
                        name="confirmEmail"
                        id="confirmEmail"
                        type="email"
                        value={userInput.confirmEmail}
                        onChange={handleFormValueChange}
                        aria-label="Confirm Email"
                        autoComplete="off"
                        required
                      />
                      {emailValidationMessage != '' && (
                        <div className={styles.alert}>
                          {emailValidationMessage}
                        </div>
                      )}
                    </div>
                    <div className="formGroup">
                      <label className={styles.formLabel}>
                        Telephone Contact{' '}
                        <span className={styles.formRequired}>*</span>
                      </label>
                      <input
                        className="formInput"
                        type="text"
                        name="telephoneContact"
                        id="telephoneContact"
                        value={userInput.telephoneContact}
                        onChange={handleFormValueChange}
                        aria-label="Telephone Contact"
                        autoComplete="off"
                        required
                        maxLength={12}
                        placeholder="###-###-####"
                        pattern="[0-9]{3}-[0-9]{3}-[0-9]{4}"
                      />
                    </div>
                    <div className="formGroup">
                      <label className={styles.formLabel}>
                        Event Type{' '}
                        <span className={styles.formRequired}>*</span>
                      </label>
                      <DropDown
                        name="eventType"
                        options={[
                          { value: 1, label: 'Medication related' },
                          {
                            value: 2,
                            label: 'Vaccine related',
                          },
                        ]}
                        onChange={handleEventTypeValueChange}
                      />
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-xs-12">
                    <hr />
                    <label className={styles.formLabel}>
                      Please describe the event or hazardous condition{' '}
                      <span className={styles.formRequired}>*</span>
                    </label>
                    <div className={styles.fieldDescription}>
                      <p>
                        Please provide as much detail about the event and associated
                        process breakdowns as possible.
                      </p>
                      <p>
                        If this problem involves a specific medication or
                        vaccine, please include the names and manufacturers of
                        each product involved. We ask that you also include
                        other pertinent information, including dosage form
                        (e.g., capsule, tablet, injection), concentration or
                        strength, and dose, that will help us to more fully
                        analyze the issue and advocate for change.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-xs-12">
                    <div className="formGroup">
                      <textarea
                        className={styles.textarea}
                        name="eventHazardousCondition"
                        id="eventHazardousCondition"
                        value={userInput.eventHazardousCondition}
                        onChange={handleFormValueChange}
                        aria-label="Please describe the event or hazardous condition"
                        rows={5}
                        autoComplete="off"
                        required
                      />
                    </div>
                    <div className="formGroup">
                      <label
                        className={styles.formLabel}
                        htmlFor="causesContributingFactors"
                      >
                        Causes and Contributing Factors{' '}
                        <span className={styles.formRequired}>*</span>
                      </label>
                      <span className={styles.fieldDescription}>
                        Please tell us the reasons (e.g., causes, contributing
                        factors) why this event happened.
                      </span>
                      <textarea
                        className={styles.textarea}
                        name="causesContributingFactors"
                        id="causesContributingFactors"
                        value={userInput.causesContributingFactors}
                        onChange={handleFormValueChange}
                        aria-label="Causes and Contributing Factors"
                        rows={5}
                        autoComplete="off"
                        required
                      />
                    </div>
                    <div className="formGroup">
                      <label
                        className={styles.formLabel}
                        htmlFor="riskReductionStrategies"
                      >
                        Risk-reduction Strategies:{' '}
                        <span className={styles.formRequired}>*</span>
                      </label>
                      <span className={styles.fieldDescription}>
                        Please describe the strategies and/or process changes
                        you put in place to prevent possible future events.
                      </span>
                      <textarea
                        className={styles.textarea}
                        name="riskReductionStrategies"
                        id="riskReductionStrategies"
                        value={userInput.riskReductionStrategies}
                        onChange={handleFormValueChange}
                        aria-label="Risk-reduction Strategies"
                        rows={5}
                        autoComplete="off"
                        required
                      />
                    </div>
                    <div className="formGroup">
                      <label className={styles.formLabel} htmlFor="fileUpload">
                        Upload Images/Files (up to 3)
                        <a
                          className={styles.uploadCaption}
                          data-tip
                          data-for="uploadTip"
                        >
                          <FontAwesomeIcon
                            className=""
                            icon={faQuestionCircle}
                          />{' '}
                          Upload requirements
                        </a>
                        <ReactTooltip
                          className={styles.uploadTooltip}
                          id="uploadTip"
                          place="right"
                          effect="solid"
                        >
                          <h4>Upload requirements:</h4>
                          <ul className={styles.bulletList}>
                            <li>Maximum 3 files.</li>
                            <li>Per file the upload limit is 12MB.</li>
                            <li>36 MB total file limit.</li>
                          </ul>
                          <p>
                            Allowed types: gif, jpg, jpeg, png, pdf, doc, docx,
                            xls, xlsx, ppt, pptx.
                          </p>
                        </ReactTooltip>
                      </label>
                      {selectedFiles.size < 3 && (
                        <input
                          className={styles.uploadField}
                          name="fileUpload"
                          id="fileUpload"
                          type="file"
                          onChange={handleFileUploadChange}
                          aria-label="Upload Images/Files (up to 3)"
                          multiple
                          value=""
                        />
                      )}
                      {[...selectedFiles.keys()].map((k) => (
                        <label
                          key={k}
                          className={styles.uploadFileItem}
                          htmlFor={k}
                        >
                          <input
                            className={styles.checkbox}
                            type="checkbox"
                            id={k}
                            checked={removeFileIds.includes(k)}
                            onChange={() => onSelectedFilesCheck(k, event)}
                            aria-label={`file-${k}`}
                          />
                          <span className={styles.uploadFileName}>
                            {selectedFiles.get(k).name}
                          </span>
                        </label>
                      ))}
                      {selectedFiles.size > 0 && (
                        <button
                          className={`${styles.formButton} accentButton`}
                          type="button"
                          onClick={handleRemoveSelectedFiles}
                        >
                          Remove Selected Files
                        </button>
                      )}
                      {validationMessage !== '' && (
                        <div className={styles.alert}>{validationMessage}</div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-xs-12">
                    {formValidationMessage != '' && (
                      <div className={styles.alert}>
                        {formValidationMessage}
                      </div>
                    )}
                  </div>
                </div>
                <div className="row">
                  <div className="col-xs-12">
                    <div className={styles.actionButtonGroup}>
                      {/* <Link className="formButton reset" onClick={this.onReset} to={"/"}>Reset</Link>
                  <Link className="formButton cancel" to={"/"}>
                    Cancel
                  </Link> */}
                      <button
                        className={`${styles.formButton} accentButton ${
                          isButtonDisabled ? styles.disabled : ''
                        }`}
                        type="submit"
                        aria-label="submit"
                        disabled={isButtonDisabled}
                      >
                        Submit
                      </button>
                      {isButtonDisabled && (
                        <div className={styles.loadingBox}>
                          <FontAwesomeIcon
                            className={styles.loadingSpinnerIcon}
                            icon={faCircleNotch}
                            spin
                          />
                          <span className={styles.loadingText}>
                            Submitting...
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </Layout>
    </div>
  );
};

export default ReportMedicationVaccineErrorForm;
